import { ReasonCode } from '../constants/error-code-constants';

import type { InputErrorKey } from './InputErrorKey.types';

export type InputError = Record<InputErrorKey, string>;

/**
 * Machine-readable constraint values for a single field, keyed by the same error keys as
 * `parameters` (e.g. `stringLengthTooShort`). Each entry maps the validator's placeholder
 * names to their values — e.g. `{ stringLengthTooShort: { min: 12, max: 128 } }` — so the
 * client can localise via interpolation ("Mindestens {{min}} Zeichen") instead of relying on
 * the pre-formatted English string in `parameters`. CIN-13969.
 */
export type InputConstraints = Record<InputErrorKey, Record<string, string | number>>;

export type ApiErrorResponse<TInputFields = undefined> = {
    isError: boolean;
    /** Absent on the synthesised responses apiResponseHandler() builds for a null/Error result. */
    message?: string;
    data: null | {
        parameters: { [field in keyof TInputFields]: InputError };
        // Parallel to `parameters`, non-breaking: present only for error keys backed by a
        // parameterized validator (StringLength, range, count, …); omitted when there are none.
        constraints?: { [field in keyof TInputFields]?: InputConstraints };
        title: string;
        type: string;
        reason_code: ReasonCode;
        detail: string;
        status: number;
    };
    status: number;
    /** The `X-Request-Id` of a traced (booking) request, as the backend echoed it (CIN-15436). */
    requestId?: string;
};

/**
 * The structural shape of an API failure, whether it is an {@link ApiError} or the plain object `orError`
 * resolves it to. Every problem+json field is optional on `data` because the client spreads whatever it
 * managed to parse: a response with no body, a non-JSON body, or a body that is not a problem+json document
 * all yield just `{ isError: true }`. Narrow before reading a field.
 */
export type ApiErrorLike = {
    isError?: boolean;
    status?: number;
    detail?: string;
    /** The `X-Request-Id` of a traced (booking) request, as the backend echoed it (CIN-15436). */
    requestId?: string;
    data?: (Partial<NonNullable<ApiErrorResponse['data']>> & { isError?: boolean }) | null;
};

const ID_SEGMENT = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * A route for a message, an event title or a grouping key: no query string (personal data, tokens), and every
 * id or opaque token segment as `:id` — a number, a UUID, or a token of 16+ characters mixing letters and digits
 * (base64url, a `payload.signature` pair or JWT, a percent-encoded value). Short slugs, locales and short ids stay.
 */
export function redactRoute(route: string): string {
    return route
        .split('?')[0]
        .split('/')
        .map((segment) =>
            ID_SEGMENT.test(segment) ||
            (segment.length >= 16 &&
                /^[A-Za-z0-9_.~%-]+$/.test(segment) &&
                /[A-Za-z]/.test(segment) &&
                /\d/.test(segment))
                ? ':id'
                : segment
        )
        .join('/');
}

/**
 * The rejection value produced by the fetch client (`fetch-helper.ts` `toFetchClientResponse`) whenever a
 * request resolves with a non-2xx status. It is an `Error`, so Sentry gets a stack trace and groups it, and it
 * keeps the top-level `status`/`isError` plus the parsed error body under `data` that every reader expects.
 * Narrow unknowns with `isApiErrorResponse` rather than `instanceof`: `orError` hands out a plain copy
 * (without `headers`), as a class instance cannot cross the server -> client component boundary.
 */
export class ApiError extends Error {
    readonly isError = true as const;
    readonly status: number;
    readonly headers: Headers;
    /** The request's HTTP method; with `route`, what Sentry groups by (every ApiError is built at the same frame). */
    readonly method: string;
    /** The request's route, already passed through {@link redactRoute}: safe to put in a tag or fingerprint. */
    readonly route: string;
    /** The `X-Request-Id` of a traced (booking) request, as the backend echoed it (CIN-15436). */
    readonly requestId?: string;
    readonly data: NonNullable<ApiErrorLike['data']>;

    constructor(init: {
        method: string;
        route: string;
        status: number;
        headers: Headers;
        requestId?: string;
        data: NonNullable<ApiErrorLike['data']>;
    }) {
        const route = redactRoute(init.route);
        super(init.data.detail || init.data.title || `${init.method} ${route} failed with ${init.status}`);
        this.name = 'ApiError';
        this.method = init.method;
        this.route = route;
        this.status = init.status;
        this.headers = init.headers;
        this.requestId = init.requestId;
        this.data = init.data;
    }
}

/**
 * Narrow an unknown thrown/rejected value to {@link ApiErrorLike}: it is one when it carries an HTTP
 * error status (>= 400) or the explicit `isError` flag the fetch client stamps on failures.
 */
export function isApiErrorResponse(value: unknown): value is ApiErrorLike {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const v = value as { status?: unknown; isError?: unknown };
    return (typeof v.status === 'number' && v.status >= 400) || v.isError === true;
}

/**
 * What `orError` (helpers/api-fallback.ts) resolves a rejection to. Its `parameters` are `never`: the rejection is not
 * typed for any input, so it fits every `ApiErrorResponse<TInput>` the caller's form-error helpers or handlers name,
 * and still narrows as an `ApiErrorLike`.
 */
export type ApiErrorFallback = Omit<ApiErrorResponse, 'data'> & {
    data: null | (Omit<NonNullable<ApiErrorResponse['data']>, 'parameters' | 'constraints'> & { parameters: never });
};

/** A JS `Error` that is not an API failure (network, abort, thrown code). An {@link ApiError} is an `Error` too, but not one of these. */
export function isNonApiError(value: unknown): value is Error {
    return value instanceof Error && !isApiErrorResponse(value);
}
